/**
 * サービス別の担当割当（ops_assignments 相当）の共有型。オペ担 / review_sql 当番はどちらも
 * 「ローテーション queue + cadence（日次/週次/月次で 1 人ずつ回る）」で表し、当番は queue から導出する
 * （導出ロジックは rotation.ts）。その上に **日付指定のカバー（`covers`）** を重ねられる: 特定の 1 日について
 * 役割ごとに担当を固定する。当日ぶんはシフト代理、将来日ぶんは休日オペたん（週末・祝日・連休など通常
 * ローテーションと別の担当を事前設定）に相当し、どちらも同じ `OpsCover`（date + role + who）で表す。
 * frontend（割当ボード）と product-api（永続化・新規依頼の自動アサイン）・ops-assignment-notify（定時通知）が
 * 同じ contract を参照するための単一 source。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 担当割当（オペ担 / review_sql 当番のローテーション queue + cadence + 日付指定カバー）の共有型と、担当候補（エンジニア一覧 + エンジニア一覧に入らない追加の担当者）の組み立て
 * @graph-connects none
 */
import { OPS_SERVICE_LABELS } from './services.js';
/**
 * ops_assignments のコレクション名。本番のみ本番名、それ以外は dev 隔離（live データに触れない）。
 * product-api と定時通知 bot が同じ env→コレクション規則を参照する単一 source（別実装だと env 判定が
 * drift して別環境の roster を読む事故になる）。
 * @graph-connects none
 */
export function opsAssignmentsCollectionName(isProduction) {
    return isProduction ? 'ops_assignments' : 'ops_assignments_dev';
}
/**
 * 保存ドキュメント（または未設定 null）を表示用 OpsAssignment に写像する。未設定サービスは空 queue +
 * cadence `'weekly'` + カバーなしの既定値（必ず 1 行返す）。serviceLabel は service から導出する。
 * @graph-connects none
 */
export function mapOpsAssignmentDoc(service, doc) {
    return {
        service,
        serviceLabel: OPS_SERVICE_LABELS[service],
        opetanQueue: doc?.opetanQueue ?? [],
        opetanCadence: doc?.opetanCadence ?? 'weekly',
        reviewerQueue: doc?.reviewerQueue ?? [],
        reviewerCadence: doc?.reviewerCadence ?? 'weekly',
        covers: doc?.covers ?? [],
    };
}
/**
 * エンジニア一覧（`/members` の engineers = PDG / ACE 所属）に入らないが、ops 依頼の担当・オペ担ローテーションの
 * 候補に出す人の nickName（Firestore `users.nickName`）。組織に所属を持たない人（CTO など）を、エンジニア看板や
 * チーム KPI に混ぜずに担当候補だけへ足すために使う。
 * @graph-connects none
 */
export const OPS_EXTRA_ASSIGNEE_NICKNAMES = ['Ryan'];
/**
 * ops の担当候補（依頼の担当ドロップダウン・割当ボードの queue / カバー）を、エンジニア一覧の nickName と
 * `OPS_EXTRA_ASSIGNEE_NICKNAMES` から作る。nickName の無い人は除き、重複を除いて名前順に並べる。
 * 依頼詳細と割当ボードが同じ候補を出すための単一 source。
 * @graph-connects OPS_EXTRA_ASSIGNEE_NICKNAMES [references] エンジニア一覧に入らない担当候補
 */
export function opsAssigneeCandidates(engineers) {
    const names = engineers
        .map((m) => m.nickName)
        .filter((n) => n !== null && n.length > 0);
    return [...new Set([...names, ...OPS_EXTRA_ASSIGNEE_NICKNAMES])].sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=assignment.js.map